import React, { useState } from 'react';
import { BarChart2, Lock, ChevronUp, ChevronDown } from 'lucide-react';
interface ForecastTableProps {
  fiscalYears?: any[];
  metrics?: any[];
  data?: any[];
  handlePeriodChange?: (period: string) => void;
}
const ForecastTable: React.FC<ForecastTableProps> = ({ fiscalYears, metrics, data, handlePeriodChange }) => {
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const periodOptions = [
    { value: 'annual', label: 'Yearly' },
    { value: 'quarter', label: 'Quarterly' }
  ];
  const [selectedPeriod, setSelectedPeriod] = useState<string>('annual');
  const getCellStyle = (metricId: string, value: string) => {
    // Skip empty values
    if (value === '-' || value === 'Upgrade' || !value) return '';

    // Handle growth columns (Revenue Growth, EPS Growth)
    if (metricId === 'revenueGrowth' || metricId === 'epsGrowth') {
      const numValue = parseFloat(value.replace('%', '').replace(',', ''));
      if (isNaN(numValue)) return '';
      if (numValue > 0) return 'text-green-500 font-semibold';
      if (numValue < 0) return 'text-red-500 font-semibold';
      return 'text-theme-muted'; // Zero growth
    }

    // Handle beat/miss columns (Revenue Beat/Miss, EPS Beat/Miss)
    if (metricId === 'revenueBeatOrMiss' || metricId === 'epsBeatOrMiss') {
      // Remove currency symbols and parse
      const cleanValue = value.replace(/[+$BMK()]/g, '').replace(',', '');
      const numValue = parseFloat(cleanValue);

      if (isNaN(numValue)) return '';
      if (numValue > 0) return 'text-green-500 font-semibold'; // Beat (positive)
      if (numValue < 0) return 'text-red-500 font-semibold'; // Miss (negative)
      return 'text-yellow-500 font-semibold'; // Exactly met expectations
    }

    return '';
  };
  const isHighlightedColumn = (year: string) => {
    return year === 'FY 2026' || year === 'FY 2027' || year === 'FY 2028';
  };
  return <div className="bg-theme-secondary rounded-lg shadow-lg overflow-hidden">
    <div className="p-2.5 flex justify-between items-center border-b border-theme-tertiary cursor-pointer transition-colors">
      <div className="flex items-center justify-between">
        <BarChart2 size={14} className="text-blue-400 mr-1.5" />
        <h2 className="text-sm font-semibold text-theme-primary">
          Analyst Forecasts
        </h2>
      </div>
      <div className="flex items-center gap-5">
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        <label style={{
          color: '#9CA3AF',
          fontSize: '14px',
          fontWeight: '500'
        }}>
          Period:
        </label>
        {periodOptions.map(option => (
          <button
            key={option.value}
            className={`btn ${selectedPeriod === option.value ? 'active' : ''}`}
            onClick={() => {handlePeriodChange(option.value || 'annual'); setSelectedPeriod(option.value);}}
            style={{
              padding: '2px 10px',
              // backgroundColor: selectedPeriod === option.value ? '#4F46E5' : 'transparent',
              // color: selectedPeriod === option.value ? 'white' : '#9CA3AF',
              // border: '1px solid #4B5563',
              borderRadius: '6px',
              fontSize: '13px',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            // onMouseEnter={(e) => {
            //   if (selectedPeriod !== option.value) {
            //     e.currentTarget.style.backgroundColor = '#374151';
            //     e.currentTarget.style.color = 'white';
            //   }
            // }}
            // onMouseLeave={(e) => {
            //   if (selectedPeriod !== option.value) {
            //     e.currentTarget.style.backgroundColor = 'transparent';
            //     e.currentTarget.style.color = '#9CA3AF';
            //   }
            // }}
          >
            {option.label}
          </button>
        ))}
      </div>
      <div  onClick={() => setIsCollapsed(!isCollapsed)} className=" bg-theme-tertiary hover:bg-theme-tertiary/70 p-2 rounded-full text-theme-muted hover:text-theme-primary transition-colors">
        {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
      </div>
      </div>
    </div>
    {!isCollapsed && <div className="overflow-x-auto scrollbar-hide">
      <table className="w-full text-left border-collapse">
        <thead>
          <tr className="bg-theme-secondary border-b border-theme-tertiary">
            <th className="p-2 font-medium text-xs text-theme-muted sticky left-0 bg-theme-secondary z-10">
              Fiscal Year
            </th>
            {metrics?.map(metric => <th key={metric.id} className={`p-2 font-medium text-center text-xs text-theme-muted ${isHighlightedColumn(metric.id) ? 'bg-blue-900/20' : ''}`}>
              {metric.locked ? <Lock size={10} className="mr-1" /> : metric.name}
            </th>)}
          </tr>
        </thead>
        <tbody>
          {fiscalYears?.map(fy =>
            <tr key={`${fy.year}-${fy.label}-${fy.date}`} className="table-row border-b border-theme-tertiary hover:bg-theme-tertiary/70 transition-colors" onMouseEnter={() => setHoveredRow(fy.year)} onMouseLeave={() => setHoveredRow(null)}>
              <td className="p-2 text-xs text-theme-primary sticky left-0 z-10 font-medium bg-theme-secondary">
                {fy.label}

              </td>
              {metrics?.map(metric => {
                const value = data && data[metric.id as keyof typeof data] && data[metric.id as keyof typeof data][fy.label] || '-';
                const cellStyle = getCellStyle(metric.id, value);
                return <td key={`${metric.id}-${fy.year}`} className={`p-2 text-center text-xs relative ${cellStyle} ${isHighlightedColumn(fy.year) ? 'bg-blue-900/20' : ''}`}>
                  {value}
                  {/* {hoveredRow === metric.id && value !== 'Upgrade' && value !== '-' && <button className="absolute right-1 top-1/2 transform -translate-y-1/2 bg-theme-tertiary hover:bg-theme-tertiary/70 p-0.5 rounded opacity-50 hover:opacity-100" title="View chart">
                  <BarChart2 size={10} className="text-theme-muted" />
                </button>} */}
                </td>;
              })}
            </tr>)}
        </tbody>
      </table>
    </div>}
  </div>;
}
export default ForecastTable;